/**
 * Command replacing a freehand stroke with the shape recognized from it.
 *
 * The freehand stroke is committed first, by its own AddEntitiesCommand; this
 * command comes right after it in the history. So the first undo brings the
 * freehand stroke back in place of the shape, and the second removes it.
 *
 * Execute: soft-delete the freehand stroke, add the shape
 * Undo: remove the shape, restore the freehand stroke
 * Redo: soft-delete the freehand stroke again, restore the shape
 */

import type { ICommand } from "./ICommand";
import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { DrawingModule } from "../types/DrawingWeb";
import { getEntityHandle } from "../core/oda";
import { getEntityLayerName } from "../entities";
import { AddEntitiesCommand } from "./AddEntitiesCommand";
import { DeleteEntityCommand } from "./DeleteEntityCommand";

export interface ReplaceWithRecognizedShapeParams {
  /** The freehand stroke entities (polyline and optional hatch) */
  freehandIds: DrawingModule.OdDbObjectId[];
  /** The recognized shape entities (shape and optional hatch) */
  shapeIds: DrawingModule.OdDbObjectId[];
  /** The layer the shape is drawn on, to restore on redo */
  originalLayerName: string;
}

export class ReplaceWithRecognizedShapeCommand implements ICommand {
  private removeFreehand: DeleteEntityCommand;
  private addShape: AddEntitiesCommand;

  constructor(
    viewer: DRDrawingViewer,
    params: ReplaceWithRecognizedShapeParams,
  ) {
    this.removeFreehand = new DeleteEntityCommand(
      viewer,
      params.freehandIds.map((entityId) => ({
        entityId,
        originalLayerName: getEntityLayerName(viewer.module, entityId),
      })),
    );
    this.addShape = new AddEntitiesCommand(viewer, {
      entityHandles: params.shapeIds.map((id) => getEntityHandle(id)),
      originalLayerName: params.originalLayerName,
    });
  }

  execute(): void {
    this.removeFreehand.execute();
    this.addShape.execute();
  }

  undo(): void {
    this.addShape.undo();
    this.removeFreehand.undo();
  }

  redo(): void {
    this.removeFreehand.redo();
    this.addShape.redo();
  }

  /**
   * Each part erases only what it left on the undo layer: the freehand stroke
   * if the replacement is applied, the shape if it was undone.
   */
  dispose(): void {
    this.removeFreehand.dispose();
    this.addShape.dispose();
  }
}
